// The fields of the view state, each with its address parameter and its text form.
//
// One table drives both the address (url.ts) and the checks on named views
// (views.ts), so a field added here is shared, validated and round-tripped
// everywhere at once. Add a field by adding a row, a property to ViewState and its
// default, and the same property to schemas/view-state.schema.json; the tests in
// web/tests/app/ check that the three agree.

import {
  DISPLAY_MODES,
  ID_PATTERN,
  MAX_CUT_PLANES,
  type Camera,
  type CutPlane,
  type DisplayMode,
  type ViewState,
  type Vec3,
} from "./view-state";

/** How one field's value is written into an address parameter and checked. */
export interface Codec<T> {
  encode(value: T): string;
  /** The value, or undefined when the text is not a valid value. */
  decode(text: string): T | undefined;
  /** Whether a JSON value (from a named view, say) is a valid value. */
  check(value: unknown): value is T;
}

export interface Field<T = unknown> {
  /** The address parameter. */
  key: string;
  /** Where the value sits in ViewState. */
  path: readonly string[];
  codec: Codec<T>;
}

// ---------------------------------------------------------------------------
// Codecs

// Plain decimal numbers only: Number() would also take "", " 1", "0x10" and "Infinity".
const NUMBER = /^-?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;

/** String(n) is the shortest text that reads back as the same double, so numbers round-trip exactly. */
function formatNumber(n: number): string {
  return String(n);
}

function parseNumber(text: string): number | undefined {
  if (!NUMBER.test(text)) return undefined;
  const n = Number(text);
  if (!Number.isFinite(n)) return undefined;
  return n === 0 ? 0 : n; // no -0: it would not survive JSON
}

interface Range {
  min?: number;
  max?: number;
  /** Excludes min itself. */
  minOpen?: boolean;
  /** Excludes max itself. */
  maxOpen?: boolean;
  integer?: boolean;
}

function inRange(n: number, r: Range): boolean {
  if (r.integer && !Number.isInteger(n)) return false;
  if (r.min !== undefined && (r.minOpen ? n <= r.min : n < r.min)) return false;
  if (r.max !== undefined && (r.maxOpen ? n >= r.max : n > r.max)) return false;
  return true;
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && !Object.is(v, -0);
}

export function number(range: Range = {}): Codec<number> {
  return {
    encode: formatNumber,
    decode(text) {
      const n = parseNumber(text);
      return n !== undefined && inRange(n, range) ? n : undefined;
    },
    check: (v): v is number => isFiniteNumber(v) && inRange(v, range),
  };
}

export const boolean: Codec<boolean> = {
  encode: (v) => (v ? "1" : "0"),
  decode(text) {
    if (text === "1" || text === "true" || text === "on") return true;
    if (text === "0" || text === "false" || text === "off") return false;
    return undefined;
  },
  check: (v): v is boolean => typeof v === "boolean",
};

export const id: Codec<string> = {
  encode: (v) => v,
  decode: (text) => (ID_PATTERN.test(text) ? text : undefined),
  check: (v): v is string => typeof v === "string" && ID_PATTERN.test(v),
};

/** One of a fixed list of words. */
export function oneOf<T extends string>(words: readonly T[]): Codec<T> {
  const is = (v: unknown): v is T => typeof v === "string" && (words as readonly string[]).includes(v);
  return {
    encode: (v) => v,
    decode: (text) => (is(text) ? text : undefined),
    check: is,
  };
}

/** null is written as "-", which no other codec produces. */
export function nullable<T>(inner: Codec<T>): Codec<T | null> {
  return {
    encode: (v) => (v === null ? "-" : inner.encode(v)),
    decode: (text) => (text === "-" ? null : inner.decode(text)),
    check: (v): v is T | null => v === null || inner.check(v),
  };
}

/** A fixed number of numbers, comma-separated. */
function numbers(count: number, ranges: Range[] = []): Codec<number[]> {
  return {
    encode: (v) => v.map(formatNumber).join(","),
    decode(text) {
      const parts = text.split(",");
      if (parts.length !== count) return undefined;
      const out: number[] = [];
      for (let i = 0; i < count; i++) {
        const n = parseNumber(parts[i]!);
        if (n === undefined || !inRange(n, ranges[i] ?? {})) return undefined;
        out.push(n);
      }
      return out;
    },
    check: (v): v is number[] =>
      Array.isArray(v) && v.length === count && v.every((n, i) => isFiniteNumber(n) && inRange(n, ranges[i] ?? {})),
  };
}

const FOV: Range = { min: 0, max: 180, minOpen: true, maxOpen: true };

/** position x,y,z, target x,y,z, fov: seven numbers. */
export const camera: Codec<Camera> = (() => {
  const seven = numbers(7, [{}, {}, {}, {}, {}, {}, FOV]);
  const vec3 = numbers(3);
  const toCamera = (n: number[]): Camera => ({
    position: [n[0]!, n[1]!, n[2]!],
    target: [n[3]!, n[4]!, n[5]!],
    fov: n[6]!,
  });
  return {
    encode: (c) => seven.encode([...c.position, ...c.target, c.fov]),
    decode(text) {
      const n = seven.decode(text);
      return n === undefined ? undefined : toCamera(n);
    },
    check(v): v is Camera {
      if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
      const c = v as Record<string, unknown>;
      return (
        Object.keys(c).length === 3 &&
        vec3.check(c["position"]) &&
        vec3.check(c["target"]) &&
        isFiniteNumber(c["fov"]) &&
        inRange(c["fov"], FOV)
      );
    },
  };
})();

function nonZero(v: Vec3): boolean {
  return v[0] !== 0 || v[1] !== 0 || v[2] !== 0;
}

/** Planes as nx,ny,nz,offset, separated by ";"; no planes is the empty text. */
export const cutaway: Codec<CutPlane[]> = (() => {
  const four = numbers(4);
  const vec3 = numbers(3);
  const checkPlane = (v: unknown): v is CutPlane => {
    if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
    const p = v as Record<string, unknown>;
    return (
      Object.keys(p).length === 2 &&
      vec3.check(p["normal"]) &&
      nonZero(p["normal"] as Vec3) &&
      isFiniteNumber(p["offset"])
    );
  };
  return {
    encode: (planes) => planes.map((p) => four.encode([...p.normal, p.offset])).join(";"),
    decode(text) {
      if (text === "") return [];
      const planes: CutPlane[] = [];
      for (const part of text.split(";")) {
        const n = four.decode(part);
        if (n === undefined) return undefined;
        const plane: CutPlane = { normal: [n[0]!, n[1]!, n[2]!], offset: n[3]! };
        if (!nonZero(plane.normal)) return undefined;
        planes.push(plane);
      }
      return planes.length <= MAX_CUT_PLANES ? planes : undefined;
    },
    check: (v): v is CutPlane[] => Array.isArray(v) && v.length <= MAX_CUT_PLANES && v.every(checkPlane),
  };
})();

/** Distinct ids, comma-separated, in their given order. */
export const idList: Codec<string[]> = {
  encode: (v) => v.join(","),
  decode(text) {
    if (text === "") return [];
    const ids = text.split(",");
    return ids.every((s) => ID_PATTERN.test(s)) && new Set(ids).size === ids.length ? ids : undefined;
  },
  check: (v): v is string[] =>
    Array.isArray(v) && v.every((s) => id.check(s)) && new Set(v).size === v.length,
};

/** id:step. */
export const lesson: Codec<{ id: string; step: number }> = {
  encode: (v) => `${v.id}:${v.step}`,
  decode(text) {
    const m = /^([a-z0-9][a-z0-9._-]*):([1-9]\d*)$/.exec(text);
    if (!m) return undefined;
    const step = Number(m[2]);
    return Number.isSafeInteger(step) ? { id: m[1]!, step } : undefined;
  },
  check(v): v is { id: string; step: number } {
    if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
    const l = v as Record<string, unknown>;
    return (
      Object.keys(l).length === 2 &&
      id.check(l["id"]) &&
      typeof l["step"] === "number" &&
      Number.isSafeInteger(l["step"]) &&
      l["step"] >= 1
    );
  },
};

// ---------------------------------------------------------------------------
// The table, in the order the address lists the parameters

const UNIT: Range = { min: 0, max: 1 };

export const FIELDS: readonly Field[] = [
  { key: "engine", path: ["engine"], codec: id },
  { key: "view", path: ["view"], codec: nullable(id) },
  { key: "throttle", path: ["controls", "throttle"], codec: number(UNIT) },
  { key: "ignition", path: ["controls", "ignition"], codec: boolean },
  { key: "volume", path: ["audio", "volume"], codec: number(UNIT) },
  { key: "muted", path: ["audio", "muted"], codec: boolean },
  { key: "listener", path: ["audio", "listener"], codec: nullable(id) },
  { key: "quality", path: ["quality"], codec: id },
  { key: "camera", path: ["camera"], codec: nullable(camera) },
  { key: "cut", path: ["cutaway"], codec: cutaway },
  { key: "time", path: ["time", "scale"], codec: number(UNIT) },
  { key: "paused", path: ["time", "paused"], codec: boolean },
  { key: "angle", path: ["time", "angle"], codec: nullable(number({ min: 0, max: 720, maxOpen: true })) },
  { key: "display", path: ["time", "display"], codec: oneOf<DisplayMode>(DISPLAY_MODES) },
  { key: "collapsed", path: ["panels", "collapsed"], codec: idList },
  { key: "lesson", path: ["lesson"], codec: nullable(lesson) },
] as Field[];

// ---------------------------------------------------------------------------
// Reading and writing a field of a state

export function getField(state: ViewState, field: Field): unknown {
  let value: unknown = state;
  for (const key of field.path) value = (value as Record<string, unknown>)[key];
  return value;
}

/** Sets a field in place; the caller owns `state` (a fresh clone). */
export function setField(state: ViewState, field: Field, value: unknown): void {
  let target = state as unknown as Record<string, unknown>;
  for (const key of field.path.slice(0, -1)) target = target[key] as Record<string, unknown>;
  target[field.path[field.path.length - 1]!] = structuredClone(value);
}
